import { useEffect, type Dispatch, type RefObject, type SetStateAction } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';

interface UseTopBarShortcutsArgs {
  navigate: NavigateFunction;
  onCloseMoreMenu: () => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
  setShowCommandPalette: Dispatch<SetStateAction<boolean>>;
  setShowCreateMenu: Dispatch<SetStateAction<boolean>>;
  setShowRecentMenu: Dispatch<SetStateAction<boolean>>;
  setShowResults: Dispatch<SetStateAction<boolean>>;
}

export function useTopBarShortcuts({
  navigate,
  onCloseMoreMenu,
  searchInputRef,
  setShowCommandPalette,
  setShowCreateMenu,
  setShowRecentMenu,
  setShowResults,
}: UseTopBarShortcutsArgs) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [setShowCommandPalette]);

  useKeyboardShortcuts([
    { key: '/', handler: () => searchInputRef.current?.focus(), description: 'Focus search' },
    { key: 'n', handler: () => setShowCreateMenu(true), description: 'Open create menu' },
    { key: 'm', combo: 'g', handler: () => navigate('/matters'), description: 'Go to Matters' },
    { key: 'p', combo: 'g', handler: () => navigate('/pipeline'), description: 'Go to Pipeline' },
    { key: 'a', combo: 'g', handler: () => navigate('/ai-hub'), description: 'Go to AI Tasks' },
    { key: 'd', combo: 'g', handler: () => navigate('/'), description: 'Go to Dashboard' },
    { key: 'c', combo: 'g', handler: () => navigate('/contacts'), description: 'Go to Contacts' },
    {
      key: 'Escape',
      handler: () => {
        setShowCreateMenu(false);
        setShowRecentMenu(false);
        setShowResults(false);
        onCloseMoreMenu();
      },
      description: 'Close dropdowns',
    },
  ]);
}
